import RobustWebSocket from 'robust-websocket'

export type WsEvent =
  | 'ping'
  | 'pong'
  | 'system'
  | 'notification'
  | 'update'
  | string

export interface WsMessage<T = any> {
  event: WsEvent
  data: T
  channel: { S: string }
}

export interface NotificationPayload {
  id: string
  title: string
  body: string
  createdAt: string
}

export type WsPayloadMap = {
  notification: NotificationPayload
  update: any
  system: { serverTime: number }
  ping: null
  pong: null
  [key: string]: any
}

export type WsHandler<E extends WsEvent> = (payload: WsPayloadMap[E]) => void
interface StoredHandler {
  eventCode: string
  callback: (data: any) => void
}

interface ISubscription {
  webSocketId: string
  bind<E extends WsEvent>(eventCode: E, handler: WsHandler<E>): WsHandler<E>
  unbind<E extends WsEvent>(handler: WsHandler<E>): void
}

class Socket {
  private socket: RobustWebSocket | null = null
  private channel: string | null = null

  private pingInterval: ReturnType<typeof setInterval> | null = null
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private lastMessageAt: number = Date.now()

  private webSocketId: string = this.generateId()
  private messageHandlers: Set<StoredHandler> = new Set()

  private generateId(length = 7): string {
    const array = new Uint8Array(length)
    crypto.getRandomValues(array)
    return Array.from(array, (x) => x.toString(36)[0]).join('')
  }

  private handleNetworkRecovery = () => {
    if (this.socket?.readyState !== WebSocket.OPEN && this.channel) {
      this.connect(this.channel)
    }
  }

  connect(channel: string) {
    if (
      this.socket &&
      this.channel === channel &&
      this.socket.readyState === WebSocket.OPEN
    ) {
      return
    }

    this.channel = channel
    this.cleanup()

    this.lastMessageAt = Date.now()

    window.addEventListener('online', this.handleNetworkRecovery)

    const API_KEY =
      process.env.WEBSOCKET_API_KEY ||
      '1Bq9xExsBdKpvivNPnbLWub6sOXdnnU4c5S5AnnQ'
    const BASE_URL = process.env.WEBSOCKET_URL

    this.socket = new RobustWebSocket(
      `${BASE_URL}/?apiKey=${API_KEY}&channel=care_portal_employee_${channel}`,
      undefined,
      {
        shouldReconnect: (_: unknown, ws) => {
          const delays = [0, 2000, 5000, 10000, 20000]
          return delays[Math.min(ws.attempts, delays.length - 1)]
        }
      }
    )

    this.socket.addEventListener('message', this.handleIncomingMessage)

    this.socket.addEventListener('open', () => {
      this.startPing()
    })

    this.startHeartbeat()
  }

  /**
   * The Master Listener: Parses JSON once, then notifies subscribers
   */
  private handleIncomingMessage = (event: MessageEvent) => {
    this.lastMessageAt = Date.now()

    const handlers = [...this.messageHandlers]

    try {
      const json = JSON.parse(event.data) as WsMessage

      if (this.channel && json.channel?.S?.includes(this.channel)) {
        handlers.forEach((handler) => {
          if (handler.eventCode === json.event) {
            handler.callback(json.data)
          }
        })
      }
    } catch (err) {}
  }

  private startPing() {
    this.stopPing()
    this.pingInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ event: 'ping' }))
      }
    }, 25000)
  }

  private stopPing() {
    if (this.pingInterval) clearInterval(this.pingInterval)
    this.pingInterval = null
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      const now = Date.now()
      const timeSinceMessage = now - this.lastMessageAt

      if (timeSinceMessage > 45000) {
        if (this.channel) {
          this.connect(this.channel)
        }
      }
    }, 5000)
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval)
    this.heartbeatInterval = null
  }

  private cleanup() {
    window.removeEventListener('online', this.handleNetworkRecovery)

    if (this.socket) {
      this.socket.removeEventListener('message', this.handleIncomingMessage)
      this.socket.close()
      this.socket = null
    }
    this.stopPing()
    this.stopHeartbeat()
  }

  disconnect() {
    this.cleanup()
    this.messageHandlers.clear()
    this.channel = null
  }

  getSubscription(): ISubscription {
    return {
      webSocketId: this.webSocketId,

      bind: <E extends WsEvent>(eventCode: E, handler: WsHandler<E>) => {
        const storedHandler: StoredHandler = {
          eventCode: eventCode as string,
          callback: handler
        }
        this.messageHandlers.add(storedHandler)

        return handler
      },

      unbind: <E extends WsEvent>(handler: WsHandler<E>) => {
        for (const item of this.messageHandlers) {
          if (item.callback === handler) {
            this.messageHandlers.delete(item)
            break
          }
        }
      }
    }
  }
}

export default new Socket()
